import { useMemo } from "react";
import type { SpeciesApi } from "@/lib/api";

interface FamilyTableProps {
  species: SpeciesApi[];
}

/** Familias botanicas derivadas do catalogo real de especies. */
export default function FamilyTable({ species }: FamilyTableProps) {
  const families = useMemo(() => {
    const acc = new Map<string, { especies: number; exemplares: number }>();
    for (const s of species) {
      if (!s.family_name) continue;
      const cur = acc.get(s.family_name) ?? { especies: 0, exemplares: 0 };
      cur.especies += 1;
      cur.exemplares += s.registered_tree_count;
      acc.set(s.family_name, cur);
    }
    return Array.from(acc.entries())
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.especies - a.especies || a.name.localeCompare(b.name));
  }, [species]);

  const semFamilia = species.filter((s) => !s.family_name).length;

  if (families.length === 0) {
    return <p className="text-sm text-foreground-500">Nenhuma família botânica informada no catálogo de espécies.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-background-200">
            <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Família</th>
            <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Espécies no catálogo</th>
            <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Exemplares cadastrados</th>
          </tr>
        </thead>
        <tbody>
          {families.map((f) => (
            <tr key={f.name} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
              <td className="py-2.5 px-3 text-foreground-900 font-medium">{f.name}</td>
              <td className="py-2.5 px-3 tabular-nums text-foreground-700">{f.especies}</td>
              <td className="py-2.5 px-3 tabular-nums text-foreground-700">{f.exemplares}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {semFamilia > 0 && (
        <p className="text-xs text-foreground-500 mt-2">{semFamilia} espécie(s) sem família informada.</p>
      )}
    </div>
  );
}
